import { useCallback, useMemo } from 'react';
import { playerTypes } from '../features/player/constants';
import { useConfiguration } from './useConfiguration';
import { formatParams } from '@intigral/utils';
import { isFreeTier } from '@intigral/utils/lib/user';
import h, { getCountry, mapOldRatingToNewRating } from '../helpers/common';
import user from '../helpers/user';
import cdnHandler from '../helpers/CDNHandler';
import type { ILocalStorageConfigurationObject } from '../types/ILocalStorage';
import { getSession, removeSession, setSession } from '@intigral/utils/lib/browser-storage/localStorage';
import browser from '../helpers/browser';
interface Asset {
    guid?: string;
    genre?: Array<string>;
    categoryType?: Array<string>;
    language: Array<string> | string;
    ratings: Array<{
        scheme: string;
        rating: string;
        subRatings: Array<any>;
    }>;

    /** Exist only for Linear assets */
    channel?: {
        adEnabled?: boolean;
        channelTitle?: string;
        guid?: string;
        category?: Array<string>;
    };

    /** Exist only for VOD assets */
    adEnabled?: boolean;
}

// Cache static device info - computed once per session
export const staticDeviceInfo = {
    device_make: browser.vendor(),
    device_model: browser.deviceType() || 'unknown',
    brand_name: browser.vendor() || 'unknown',
    os_string: browser.os() || 'unknown',
    ua: navigator.userAgent || 'unknown',
} as const;

let lastCSAISupplyParameters: Record<string, any> | null = null;
let lastCSAIAdBreakUrls: string[] | null = null;

const useCSAI = (asset: Asset, playerType: keyof typeof playerTypes, enableFor: 'LINEAR' | 'VOD' | 'ALL') => {
    const config = useConfiguration().data;

    const userInfo = user.getUserInfo();
    const isFreeTierSub = isFreeTier(userInfo);
    const isLinear = playerType === playerTypes.LiveTV;
    const isVOD = playerType === playerTypes.Vod;

    const isAdsEnabledForCSAI = useMemo(() => {
        // Ads are for free tiers only
        if (!isFreeTierSub) return false;

        // sessionValue may be 'true' or the asset GUID to disable ads
        const sessionValue: string = getSession('noAds');
        const hasValidSessionValue = typeof sessionValue === 'string' && sessionValue.length > 0;

        const adEnabled = isLinear ? asset?.channel?.adEnabled : asset?.adEnabled;
        if (!adEnabled) {
            // handle case where ads are disabled for this asset but session storage indicates otherwise
            if (hasValidSessionValue) {
                // Clear invalid/no longer needed session storage.
                removeSession('noAds');
            }

            return false;
        }

        if (hasValidSessionValue) {
            const currentAssetGUID = isLinear ? asset?.channel?.guid : asset?.guid;
            const hasValidAssetGUID = typeof currentAssetGUID === 'string' && currentAssetGUID.length > 0;

            if (hasValidAssetGUID) {
                if (sessionValue === 'true') {
                    // Disable ads for the current GUID
                    setSession('noAds', currentAssetGUID);
                    return false;
                }

                const shouldNotPlayAds = sessionValue === currentAssetGUID;
                if (shouldNotPlayAds) {
                    // Disable ads for this asset
                    return false;
                } else {
                    // Clear invalid/no longer needed session storage.
                    // May play ads depending on the 'enableFor' parameter.
                    removeSession('noAds');
                }
            }
        }

        return enableFor === 'ALL' || (enableFor === 'LINEAR' && isLinear) || (enableFor === 'VOD' && isVOD);
    }, [asset, playerType]);

    const language = useMemo(() => {
        const preferredAudioLanguage: string = user.getPreferredAudioLanguage().toLowerCase();
        const assetLanguage = asset?.language;

        if (typeof assetLanguage === 'string') {
            return assetLanguage.toLowerCase() === preferredAudioLanguage ? preferredAudioLanguage : 'ar';
        } else if (Array.isArray(assetLanguage)) {
            return assetLanguage.some((lang: string) => lang.toLowerCase() === preferredAudioLanguage)
                ? preferredAudioLanguage
                : 'ar';
        } else {
            return 'ar';
        }
    }, [asset]);

    // Supply parameters for CSAI
    const supplyParameters = useMemo(() => {
        const params = {
            w: window.innerWidth.toString(),
            h: window.innerHeight.toString(),
            url: config.avodConfigs.adTagParams.domain,
            app_bundle: config.avodConfigs.adTagParams.appBundle,
            app_name: config.avodConfigs.adTagParams.appName,
            country: getCountry() as string,
            did: user.getUserDeviceId() as string,
            ifa_type: config.avodConfigs.adTagParams.ifaType,
            content_id: asset?.guid ?? asset?.channel?.guid ?? '',
            content_genre: (asset?.genre || asset?.channel?.category || []).join(','),
            language: language,
            pod_max_dur: (config.avodConfigs.adTagParams.pod_max_dur || 90).toString(),
            coppa: asset?.categoryType?.includes('kids') ? '1' : '0',
            rating: mapOldRatingToNewRating(asset?.ratings?.[0]?.rating),
            ip: h.getUserIP() as string,
            ...staticDeviceInfo,
            ...(isLinear ? { channel_name: asset?.channel?.channelTitle ?? '' } : {}),
            ...(!isVOD && {
                dnt: (config.avodConfigs.adTagParams.dnt || 0).toString(),
                lmt: (config.avodConfigs.adTagParams.lmt || 0).toString(),
            }),
        };
        lastCSAISupplyParameters = params;

        return params;
    }, [asset, language]);

    // Ad breaks configuration for CSAI
    const adBreaksForCSAI: ILocalStorageConfigurationObject['player']['adBreaks'] = useMemo(() => {
        const updatedAdBreaks =
            config?.player?.adBreaks.map((adBreak) => {
                const newURL = new URL(adBreak.tag.url);
                for (const [paramKey, paramValue] of Object.entries(supplyParameters)) {
                    newURL.searchParams.set(paramKey, paramValue.toString());
                }
                adBreak.tag.url = newURL.toString();
                return adBreak;
            }) || [];
        lastCSAIAdBreakUrls = updatedAdBreaks.map((adBreak) => adBreak.tag.url);
        return updatedAdBreaks;
    }, [supplyParameters]);

    const setupAdsParamsForCSAI = useCallback(() => {
        const adsParams = ((config as any)?.video?.smartlibParams?.adsParams ?? {}) as Record<string, string>;
        const cdnUrlParams = config?.avodConfigs?.urlParams;

        const formattedCdnAdsParams = formatParams(adsParams, {
            currentDate: Date.now().toString(),
            deviceId: supplyParameters.did,
            width: supplyParameters.w,
            height: supplyParameters.h,
            country: supplyParameters.country,
            language: supplyParameters.language,
            contentGenre: supplyParameters.content_genre,
            isKids: supplyParameters.coppa,
            contentId: supplyParameters.content_id,
            ...staticDeviceInfo,
            ...(isLinear
                ? {
                      CHANNEL_NAME: supplyParameters.channel_name,
                      DNT: supplyParameters.dnt,
                      LMT: supplyParameters.lmt,
                  }
                : {}),
            pod_max_dur: supplyParameters.pod_max_dur,
            ip: supplyParameters.ip,
        });

        // cdnHandler.setupAdvertising(formattedCdnAdsParams, cdnUrlParams);
    }, [asset, supplyParameters]);

    return {
        isAdsEnabledForCSAI,
        adBreaksForCSAI,
        setupAdsParamsForCSAI,
    };
};

export default useCSAI;

export const getLastCSAISupplyParameters = () => lastCSAISupplyParameters;
export const getLastCSAIAdBreakUrls = () => lastCSAIAdBreakUrls;
